/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { get } from '../../../services/GenesisApi';
import {
  CPTY_STATEMENTS_COLUMNS,
  CPTY_STATEMENT_CONFIG,
  UPLOADER_CPTY_STATEMENTS_COLUMNS,
  UPLOADER_CPTY_STATEMENT_CONFIG,
} from '../../../constants/CounterpartyStatementsConfig';

interface ICounterpartyData {
  id: number;
  counterpartyName: string;
}

interface IStatementColumns {
  id: number;
  name: string;
  unit: string;
}

interface ICounterpartyStatementComlumns {
  id: number;
  counterpartyName: string;
  statementColumns: IStatementColumns[];
}

interface IStatementColumnsPayload {
  counterparty: ICounterpartyData;
  statementColumns: IStatementColumns[];
}

export interface IStatementConfig {
  configId: number;
  counterpartyName: string;
  statementColumn: string;
  value: number;
  unit: string;
  startDate: string;
  endDate: string;
}

export interface IUploaderStatementConfig {
  configId: number;
  counterpartyName: string;
  statementColumn: string;
  zkey: string;
  startDate: string;
  endDate: string;
}

interface IStatementsConfigPayload {
  counterpartyStatement: IStatementConfig[];
  counterparty: ICounterpartyData[];
}

interface IUploaderConfigPayload {
  counterpartyStatement: IUploaderStatementConfig[];
  counterparty: ICounterpartyData[];
}

export const getStatementColumns = createAsyncThunk(
  'counterpartyStatementsConfig/getStatementColumns',
  async () => {
    return get(CPTY_STATEMENTS_COLUMNS, {})
      .then((response) => {
        return response.data ?? [];
      })
      .catch((error) => {
        throw error;
      });
  }
);

export const getCounterpartyStatementsConfig = createAsyncThunk(
  'counterpartyStatementsConfig/getCounterpartyStatementsConfig',
  async (month: string) => {
    return get(`${CPTY_STATEMENT_CONFIG}?monthDay=${month}`, {})
      .then((response) => {
        return response.data ?? [];
      })
      .catch((error) => {
        throw error;
      });
  }
);

export const getUploaderStatementColumns = createAsyncThunk(
  'counterpartyStatementsConfig/getUploaderStatementColumns',
  async () => {
    return get(UPLOADER_CPTY_STATEMENTS_COLUMNS, {})
      .then((response) => {
        return response.data ?? [];
      })
      .catch((error) => {
        throw error;
      });
  }
);

export const getCounterpartyStatementsUploaderConfig = createAsyncThunk(
  'counterpartyStatementsConfig/getCounterpartyStatementsUploaderConfig',
  async (month: string) => {
    return get(`${UPLOADER_CPTY_STATEMENT_CONFIG}?monthDay=${month}`, {})
      .then((response) => {
        return response.data ?? [];
      })
      .catch((error) => {
        throw error;
      });
  }
);

export interface ICounterpartyStatementsConfigState {
  counterpartyStatementsColumns: ICounterpartyStatementComlumns[];
  isLoading: boolean;
  isLoadingConfig: boolean;
  statementConfig: IStatementConfig[];
  statementConfigCounterparty: ICounterpartyData[];
  uploaderStatementsColumn: ICounterpartyStatementComlumns[];
  uploaderStatementsConfig: IUploaderStatementConfig[];
  uploaderStatementsConfigCounterparty: ICounterpartyData[];
}

export const initialState: ICounterpartyStatementsConfigState = {
  counterpartyStatementsColumns: [],
  isLoading: false,
  isLoadingConfig: false,
  statementConfig: [],
  statementConfigCounterparty: [],
  uploaderStatementsColumn: [],
  uploaderStatementsConfig: [],
  uploaderStatementsConfigCounterparty: [],
};

const CounterpartyStatementsConfigData = createSlice({
  name: 'counterpartyStatementsConfigActions',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(
        getStatementColumns.pending,
        (state: ICounterpartyStatementsConfigState) => {
          state.isLoading = true;
        }
      )
      .addCase(
        getStatementColumns.fulfilled,
        (
          state: ICounterpartyStatementsConfigState,
          { payload }: PayloadAction<IStatementColumnsPayload[]>
        ) => {
          state.counterpartyStatementsColumns = payload.map((item) => ({
            id: item.counterparty.id,
            counterpartyName: item.counterparty.counterpartyName,
            statementColumns: item.statementColumns,
          }));
          state.isLoading = false;
        }
      )
      .addCase(
        getStatementColumns.rejected,
        (state: ICounterpartyStatementsConfigState) => {
          state.isLoading = false;
        }
      )
      .addCase(
        getCounterpartyStatementsConfig.pending,
        (state: ICounterpartyStatementsConfigState) => {
          state.isLoadingConfig = true;
        }
      )
      .addCase(
        getCounterpartyStatementsConfig.fulfilled,
        (
          state: ICounterpartyStatementsConfigState,
          { payload }: PayloadAction<IStatementsConfigPayload>
        ) => {
          state.isLoadingConfig = false;
          state.statementConfig = payload?.counterpartyStatement;
          state.statementConfigCounterparty = (
            payload?.counterparty || []
          ).sort((a, b) =>
            a.counterpartyName.localeCompare(b.counterpartyName)
          );
        }
      )
      .addCase(
        getCounterpartyStatementsConfig.rejected,
        (state: ICounterpartyStatementsConfigState) => {
          state.isLoadingConfig = false;
        }
      )
      .addCase(
        getUploaderStatementColumns.pending,
        (state: ICounterpartyStatementsConfigState) => {
          state.isLoading = true;
        }
      )
      .addCase(
        getUploaderStatementColumns.fulfilled,
        (
          state: ICounterpartyStatementsConfigState,
          { payload }: PayloadAction<IStatementColumnsPayload[]>
        ) => {
          state.uploaderStatementsColumn = payload.map((item) => ({
            id: item.counterparty.id,
            counterpartyName: item.counterparty.counterpartyName,
            statementColumns: item.statementColumns,
          }));
          state.isLoading = false;
        }
      )
      .addCase(
        getUploaderStatementColumns.rejected,
        (state: ICounterpartyStatementsConfigState) => {
          state.isLoading = false;
        }
      )
      .addCase(
        getCounterpartyStatementsUploaderConfig.pending,
        (state: ICounterpartyStatementsConfigState) => {
          state.isLoadingConfig = true;
        }
      )
      .addCase(
        getCounterpartyStatementsUploaderConfig.fulfilled,
        (
          state: ICounterpartyStatementsConfigState,
          { payload }: PayloadAction<IUploaderConfigPayload>
        ) => {
          state.isLoadingConfig = false;
          state.uploaderStatementsConfig = payload?.counterpartyStatement;
          state.uploaderStatementsConfigCounterparty = (
            payload?.counterparty || []
          ).sort((a, b) =>
            a.counterpartyName.localeCompare(b.counterpartyName)
          );
        }
      )
      .addCase(
        getCounterpartyStatementsUploaderConfig.rejected,
        (state: ICounterpartyStatementsConfigState) => {
          state.isLoadingConfig = false;
        }
      );
  },
});

export const counterpartyStatementsCongigActions =
  CounterpartyStatementsConfigData.actions;
export default CounterpartyStatementsConfigData.reducer;
